import style from '../components/ErpDialogPositions.module.scss';

import { TFunction } from 'i18next';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { NUMBER_PARAMS } from '@work4all/components';
import { EditableCell } from '@work4all/components/lib/dataDisplay/basic-table/components/row-render/components/editable-cell/EditableCell';

import { useUser } from '@work4all/data';

import { Position } from '@work4all/models/lib/Classes/Position.entity';

import { currencyAsSign, parseAsFloat } from '@work4all/utils';
import { canSeeSellingPrices } from '@work4all/utils/lib/permissions';

import {
  EditTableColumns,
  OnEditPosition,
} from '../../../../containers/mask-overlays/mask-overlay/views/erp/components/tab-panels/positions/components/edit-table/types';
import { useErpDialogTargetOptions } from '../ErpDialogTargetOptionsProvider';
import { useErpMask } from '../hooks/use-erp-mask';
import {
  ErpConversionGroupByType,
  ErpObject,
  GroupedPosition,
  OnRefresh,
} from '../types';

import {
  makeAmountColumn,
  makeArticleNumberColumn,
  makeDescriptionColumn,
  makeDiscountColumn,
  makeInsteadOfPriceColumn,
  makeNavigateColumn,
  makeNumberColumn,
  makePriceColumn,
  makeShouldColumn,
  makeSupplierColumn,
  makeTotalPriceColumn,
  makeUnitColumn,
} from './ErpDialogArticleGroupedPositions/columns-utils';
import { useColumnsWidth } from './use-column-settings';

interface UseErpDialogPositionColumnsProps {
  onEditPosition: (result: OnEditPosition<Position>) => void;
  data: ErpObject[];
  groupByType: ErpConversionGroupByType;
  contractToDeliveryNote: boolean;
  onRefresh: OnRefresh;
  isEditMode: boolean;
  onRefetch: () => void;
}

export const useErpDialogPositionColumns = ({
  onEditPosition,
  data,
  contractToDeliveryNote,
  groupByType,
  isEditMode,
  onRefetch,
}: UseErpDialogPositionColumnsProps) => {
  const { t } = useTranslation();

  const user = useUser();

  // We need to set this supplier per article
  const onEditPositionWithContext = useCallback(
    (result: OnEditPosition<Position>) => {
      onEditPosition(result);
    },
    [onEditPosition]
  );

  const formatTitleWithCurrency = useCallback(
    (title: string) => {
      return data[0]?.currency
        ? `${title} ${currencyAsSign(data[0]?.currency.name)}`
        : title;
    },
    [data]
  );

  const updateWitdth = useColumnsWidth(groupByType);
  const openMask = useErpMask(data, onRefetch);

  const { invoiceAsCreditNote } = useErpDialogTargetOptions();
  const showNegativeAmounts = Boolean(invoiceAsCreditNote);

  const columns = useMemo(() => {
    if (groupByType === 'CONTRACT') {
      const cols = [
        makeNumberColumn(t),
        makeNavigateColumn(openMask),
        makeDescriptionColumn(
          t,
          undefined,
          t('COMMON.ERP.DESCRIPTION_CONVERSION')
        ),
        makeArticleNumberColumn(t),
        makeAmountColumn(t, onEditPositionWithContext, 84, true, !isEditMode),
        makeSupplierColumn(
          t,
          onEditPositionWithContext,
          undefined,
          undefined,
          !isEditMode
        ),
        makeUnitColumn(t, 56, onEditPositionWithContext, false, !isEditMode),
        makePriceColumn(
          t,
          (x) => x,
          98,
          true,
          isEditMode ? onEditPositionWithContext : undefined
        ),
        makeDiscountColumn(
          t,
          onEditPositionWithContext,
          undefined,
          !isEditMode
        ),
        makeTotalPriceColumn(t, (x) => x, 98, true),
      ];
      updateWitdth(cols);
      return cols;
    }

    let columns: EditTableColumns<GroupedPosition>[] = [makeNumberColumn(t)];
    if (contractToDeliveryNote) {
      columns = [
        ...columns,
        makeShouldColumn(t, style.backgroundCell, true),
        makeRestColumn(t, true),
        makeAmountColumn(
          t,
          onEditPosition,
          84,
          false,
          false,
          contractToDeliveryNote
        ),
        makeUnitColumn(t, 56, onEditPosition, contractToDeliveryNote, true),
        makeDescriptionColumn(t),
      ];
    } else {
      columns = [
        ...columns,
        makeDescriptionColumn(t),
        {
          Header: t('COMMON.ERP.AMOUNT'),
          accessor: 'amount',
          width: 84,
          type: 'number',
          transformInputValue: (input: string) => {
            const number = parseAsFloat(input);
            if (number < 0) return '0';
            return input;
          },
          ...NUMBER_PARAMS,
          Cell: (cell) => {
            return (
              <EditableCell
                {...cell}
                value={showNegativeAmounts ? -cell.value : cell.value}
              />
            );
          },
        },
        makeUnitColumn(t, 56, onEditPosition, false, true),
      ];
    }
    if (canSeeSellingPrices(user)) {
      columns = [
        ...columns,
        makePriceColumn(t, formatTitleWithCurrency),
        makeTotalPriceColumn(t, formatTitleWithCurrency),
        makeInsteadOfPriceColumn(t),
      ];
    }
    updateWitdth(columns);

    return columns;
  }, [
    groupByType,
    t,
    contractToDeliveryNote,
    user,
    onEditPosition,
    formatTitleWithCurrency,
    updateWitdth,
    onEditPositionWithContext,
    isEditMode,
    openMask,
    showNegativeAmounts,
  ]);
  return columns;
};

const makeRestColumn = (
  t: TFunction,
  contractToDeliveryNote: boolean
): EditTableColumns<GroupedPosition> => ({
  Header: t('COMMON.ERP.REST'),
  accessor: 'backlog',
  type: contractToDeliveryNote ? 'number' : undefined,
  width: 56,
  className: style.backgroundCell,
  ...NUMBER_PARAMS,
});
